import { QueryClient } from "@tanstack/react-query";
import { createRouter } from "@tanstack/react-router";
import * as Sentry from "@sentry/tanstackstart-react";
import { routeTree } from "./routeTree.gen";
import { installStorageFallback } from "./lib/safe-storage";
import { installStaleChunkRecovery } from "./lib/chunk-recovery";
import { APP_RELEASE, getAppEnvironment } from "./lib/app-release";

export const getRouter = () => {
  // Safari with cookies/storage blocked throws on `window.localStorage` access.
  // Install an in-memory shim before any app or vendor code touches it.
  installStorageFallback();
  // Recover from stale cached chunks served after a deploy (common in Safari).
  installStaleChunkRecovery();

  const queryClient = new QueryClient();

  const router = createRouter({
    routeTree,
    context: { queryClient },
    scrollRestoration: true,
    defaultPreloadStaleTime: 0,
  });

  if (!router.isServer) {
    // Observability must never be able to stop the app booting (Safari privacy
    // settings can make replay/storage init throw).
    try {
      Sentry.init({
        dsn: "https://acd7fa7439990158707fe53f812aa554@o4511954249252864.ingest.de.sentry.io/4511954363547728",
        // Correlate every event, log and replay to the exact build and deployment.
        release: APP_RELEASE,
        environment: getAppEnvironment(),
        dataCollection: {
          // Deliberate decision, not a framework default: error events must never
          // carry customer PII or contract values, so request/response bodies and
          // automatic user info are both switched off. Identity is attached
          // explicitly and minimally in src/lib/sentry-user.ts instead.
          // https://docs.sentry.io/platforms/javascript/guides/tanstackstart-react/configuration/options/#dataCollection
          userInfo: false,
          httpBodies: [],
        },
        // Enables the structured logs product so console output shows up in Sentry Logs.
        enableLogs: true,
        integrations: [
          Sentry.tanstackRouterBrowserTracingIntegration(router),
          Sentry.replayIntegration({
            // Redact text and media by default so no customer data leaves the app.
            maskAllText: true,
            blockAllMedia: true,
          }),
          // Reports window.onerror and unhandled promise rejections as errors.
          Sentry.globalHandlersIntegration({
            onerror: true,
            onunhandledrejection: true,
          }),
          // console.error/warn become Sentry issues with matching severity,
          // and every console call is kept as a breadcrumb by default.
          Sentry.captureConsoleIntegration({ levels: ["error", "warn"] }),
          // Streams console output to Sentry Logs with the right log level.
          Sentry.consoleLoggingIntegration({
            levels: ["error", "warn", "info", "debug"],
          }),
          // Attaches non-standard properties on thrown error objects to the issue.
          Sentry.extraErrorDataIntegration({ depth: 5 }),
        ],
        tracesSampleRate: 1.0,
        // Record 10% of all sessions, plus 100% of sessions where an error occurs.
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
      });
    } catch (error) {
      console.error(error);
    }
  }

  return router;
};
